import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DesignElement } from "./types";
import { FaFont, FaSquare, FaBorderStyle, FaTrash, FaEye, FaEyeSlash } from "react-icons/fa";

interface LayerTreeProps {
  elements: DesignElement[];
  selectedElement: string | null;
  onSelectElement: (id: string | null) => void;
  onDeleteElement: (id: string) => void;
  onUpdateElement: (id: string, updates: Partial<DesignElement>) => void;
}

interface LayerItemProps {
  element: DesignElement;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onToggleVisible: () => void;
}

const getElementIcon = (type: string) => {
  switch (type) {
    case "text":
      return <FaFont className="w-4 h-4 text-blue-500" />;
    case "shape":
      return <FaSquare className="w-4 h-4 text-purple-500" />;
    case "frame":
      return <FaBorderStyle className="w-4 h-4 text-orange-500" />;
    default:
      return <FaSquare className="w-4 h-4 text-gray-400" />;
  }
};

const getElementLabel = (element: DesignElement): string => {
  if (element.type === "text" && element.content) {
    return `Text: ${element.content.substring(0, 30)}${element.content.length > 30 ? "..." : ""}`;
  }
  if (element.type === "shape" && element.content) {
    return "Shape";
  }
  return `${element.type.charAt(0).toUpperCase() + element.type.slice(1)}`;
};

const SortableLayerItem: React.FC<LayerItemProps & { id: string }> = ({
  element,
  isSelected,
  onSelect,
  onDelete,
  onToggleVisible,
  id,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isVisible = element.style.opacity !== 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded
        ${isSelected ? "bg-blue-100 border-l-2 border-blue-500" : ""}
      `}
      onClick={onSelect}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </div>
      <div className="flex-1 flex items-center gap-2">
        {getElementIcon(element.type)}
        <span className="flex-1 truncate">{getElementLabel(element)}</span>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisible();
        }}
        className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700"
        title={isVisible ? "Hide" : "Show"}
      >
        {isVisible ? <FaEye className="w-3 h-3" /> : <FaEyeSlash className="w-3 h-3" />}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="p-1 hover:bg-red-100 rounded text-gray-500 hover:text-red-600"
        title="Delete"
      >
        <FaTrash className="w-3 h-3" />
      </button>
    </div>
  );
};

export const LayerTree: React.FC<LayerTreeProps> = ({
  elements,
  selectedElement,
  onSelectElement,
  onDeleteElement,
  onUpdateElement,
}) => {
  const elementIds = [...elements].reverse().map((el) => el.id);

  return (
    <div className="p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Layers</h3>
      <SortableContext items={elementIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {elements.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-8">No elements yet</div>
          ) : (
            [...elements].reverse().map((element) => (
              <SortableLayerItem
                key={element.id}
                id={element.id}
                element={element}
                isSelected={selectedElement === element.id}
                onSelect={() => onSelectElement(element.id)}
                onDelete={() => onDeleteElement(element.id)}
                onToggleVisible={() => {
                  onUpdateElement(element.id, {
                    style: {
                      ...element.style,
                      opacity: element.style.opacity === 0 ? 1 : 0,
                    },
                  });
                }}
              />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
};

export default LayerTree;

